import React, { useState } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { SpotlightTarget } from '@atlaskit/onboarding';

import { DialogsStateContainer } from '@confluence/dialogs';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import {
	PAGE_BUTTONS,
	TOOLS_MENU_CONTAINER,
	useWebItemLocation,
} from '@confluence/web-item-location';

import { ANALYTICS_ADDON_ACTIONS_MENU_ID, CONNECT_APP_PREFIX_KEY } from '@confluence/web-item';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { isLivePagesExtensionPointsEnabled } from '@confluence/live-pages-utils/entry-points/isLivePagesExtensionPointsEnabled';
import { useBooleanFeatureFlag } from '@confluence/session-data';
import { ForgeContentTools } from '@confluence/content-tools';
import { LinkToThisPageDialog } from '@confluence/link-to-this-page-dialog';
import {
	LINK_TO_THIS_PAGE_SHORTCUT,
	GeneralShortcutListener,
	VIEW_ATTACHMENTS_SHORTCUT,
} from '@confluence/shortcuts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';

import { CommonDropdownMenu } from './CommonDropdownMenu';
import {
	ViewChangesItem,
	CopyPageItem,
	DeletePageItem,
	EditLabelItem,
	MovePageItem,
	ResolvedCommentsItem,
	ExportMenuItem,
	PageHistoryMenuItem,
	AnalyticsItem,
	ChangePageOwnershipItem,
	AdvancedDetailsMenuItem,
	PublishItem,
	AttachmentsMenuItem,
	ArchiveLinkMenuItem,
	TemplateMenuItem,
	PageAppsMenuItem,
} from './LivePagesEllipsisMenuItems';
import type { LivePagesEllipsisDropdownComponentProps } from './types';
import { Buttons } from './types';
import { LivePagesEllipsisDropdownPlaceholder } from './LivePagesEllipsisDropdownPlaceholder';

enum GroupItemsKey {
	PUBLISH = 'publish',
	OWNERSHIP = 'ownership',
	PAGEACTIONS = 'pageactions',
	PAGEAPPS = 'pageapps',
	OTHER = 'other',
}
enum SubMenuTypes {
	Export = 'export',
	Advanced = 'advanced',
	Attachments = 'attachments',
	Apps = 'apps',
	ContentOwnership = 'content-ownership',
}

type LivePageMenuItem = {
	allowed: boolean;
	component: React.ReactNode;
	isSubMenu?: boolean;
};

export const LivePagesEllipsisDropdownComponent = ({
	contentId,
	spaceKey,
	retrieveEditorTitleAndContent,
	allowedEllipsisButtons,
	onClickPublish,
	saveDraft,
	isViewMode,
	onClickOpenContentCreationPanel,
	isInitialMenuOpen = false,
}: LivePagesEllipsisDropdownComponentProps) => {
	const [isMenuOpen, setIsMenuOpen] = useState(isInitialMenuOpen);
	const [openSubMenu, setOpenSubMenu] = useState<SubMenuTypes | null>(null);
	const { push } = useRouteActions();
	const isContentOwnerSupportedForAllTypes = useBooleanFeatureFlag(
		'confluence.frontend.content.owner.support.all.types',
	);
	const isEditorActionBarSSREnabled = useBooleanFeatureFlag(
		'confluence.frontend.ssr-editor-actions-bar',
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		loading: webItemLoading,
		error: webItemError,
		webItems,
	} = useWebItemLocation({
		location: [TOOLS_MENU_CONTAINER, PAGE_BUTTONS],
		contentId,
	});

	if (webItemLoading && isEditorActionBarSSREnabled) {
		return <LivePagesEllipsisDropdownPlaceholder />;
	}

	const isWebItemAvailable = !webItemLoading && !webItemError;

	const isAllowed = (menuItem: Buttons) => allowedEllipsisButtons?.includes(menuItem) ?? true;

	const handleMenuOpenChange = ({ isOpen }) => {
		setIsMenuOpen(isOpen);

		if (isMenuOpen) {
			createAnalyticsEvent?.({
				type: 'sendScreenEvent',
				data: {
					name: 'livePagesEllipsisDropdownForMoreItems',
				},
			}).fire();
		} else {
			closeAllSubMenus();
		}
	};

	const handleSubMenuMouseEnter = (submenuType: SubMenuTypes) => {
		setOpenSubMenu(submenuType);
	};

	const handleSubMenuItemClick = (submenuType: SubMenuTypes) => {
		if (openSubMenu === submenuType) {
			setOpenSubMenu(null);
		} else {
			setOpenSubMenu(submenuType);
		}
	};

	const closeAllSubMenus = () => {
		setOpenSubMenu(null);
	};

	const closeMenu = () => {
		setIsMenuOpen(false);
	};

	const makeLinkToThisPageDialogOpener = (dialogs) => () => {
		dialogs.showModal(LinkToThisPageDialog, { contentId });
	};

	const navigateToAttachments = () => {
		const viewAttachmentsWebItem = webItems.find(
			(webItem) => webItem.id === 'view-attachments-link',
		);
		if (viewAttachmentsWebItem?.url) {
			push(viewAttachmentsWebItem?.url);
		}
	};

	const menuItemWrapper = (children) => <div onMouseEnter={closeAllSubMenus}>{children}</div>;

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const getOwnershipGroupItems = (dialogs): LivePageMenuItem[] => [
		{
			component: <ViewChangesItem contentId={contentId} dialogs={dialogs} saveDraft={saveDraft} />,
			allowed: !isViewMode && isAllowed(Buttons.VIEW_CHANGES),
		},
		{
			component: (
				<ChangePageOwnershipItem
					webItems={webItems}
					contentId={contentId}
					closeMenu={closeMenu}
					isOpen={openSubMenu === SubMenuTypes.ContentOwnership}
					onClick={() => handleSubMenuItemClick(SubMenuTypes.ContentOwnership)}
					onMouseEnter={() => handleSubMenuMouseEnter(SubMenuTypes.ContentOwnership)}
					closePopup={() => setOpenSubMenu(null)}
				/>
			),
			isSubMenu: isContentOwnerSupportedForAllTypes,
			allowed: !isViewMode,
		},
		{
			component: <EditLabelItem contentId={contentId} spaceKey={spaceKey} dialogs={dialogs} />,
			allowed: !isViewMode && isAllowed(Buttons.EDIT_LABELS) && !isCompanyHub,
		},
	];

	const getPageActionGroupItems = (dialogs): LivePageMenuItem[] => [
		{
			component: <CopyPageItem contentId={contentId} spaceKey={spaceKey} dialogs={dialogs} />,
			allowed: !isViewMode,
		},
		{
			component: (
				<MovePageItem
					contentId={contentId}
					spaceKey={spaceKey}
					retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
				/>
			),
			allowed: !isViewMode && isAllowed(Buttons.MOVE_PAGE) && !isCompanyHub,
		},
		{
			component: (
				<ExportMenuItem
					isSubMenuOpen={openSubMenu === SubMenuTypes.Export}
					handleMouseEnter={() => handleSubMenuMouseEnter(SubMenuTypes.Export)}
					handleClick={() => handleSubMenuItemClick(SubMenuTypes.Export)}
					webItems={webItems}
				/>
			),
			isSubMenu: true,
			allowed: isAllowed(Buttons.EXPORT) && isWebItemAvailable,
		},
		{
			component: (
				<ArchiveLinkMenuItem contentId={contentId} webItems={webItems} dialogs={dialogs} />
			),
			allowed: !isViewMode && isAllowed(Buttons.ARCHIVE) && isWebItemAvailable,
		},
		{
			component: <DeletePageItem contentId={contentId} webItems={webItems} dialogs={dialogs} />,
			allowed: !isViewMode && isWebItemAvailable,
		},
	];

	const getPageAppsGroupItems = (forgeMenuItems): LivePageMenuItem[] => {
		const pageAppItems = webItems.filter(
			(item) =>
				item?.completeKey?.includes(CONNECT_APP_PREFIX_KEY) &&
				item?.id !== ANALYTICS_ADDON_ACTIONS_MENU_ID,
		);

		return [
			{
				component: (
					<PageAppsMenuItem
						isSubMenuOpen={openSubMenu === SubMenuTypes.Apps}
						handleMouseEnter={() => handleSubMenuMouseEnter(SubMenuTypes.Apps)}
						handleClick={() => handleSubMenuItemClick(SubMenuTypes.Apps)}
						webItems={pageAppItems}
						forgeMenuItems={forgeMenuItems}
					/>
				),
				isSubMenu: true,
				allowed:
					isLivePagesExtensionPointsEnabled() &&
					(pageAppItems.length > 0 || forgeMenuItems.length > 0),
			},
		];
	};

	const getPublishGroupItems = (): LivePageMenuItem[] => [
		{
			component: <PublishItem onClickPublish={onClickPublish} />,
			allowed: !isViewMode,
		},
		{
			component: (
				<TemplateMenuItem onClickOpenContentCreationPanel={onClickOpenContentCreationPanel} />
			),
			allowed: !isViewMode,
		},
	];

	const getOtherGroupItems = (dialogs): LivePageMenuItem[] => [
		{
			component: (
				<ResolvedCommentsItem contentId={contentId} spaceKey={spaceKey} dialogs={dialogs} />
			),
			allowed: isAllowed(Buttons.RESOLVED_COMMENTS),
		},
		{
			component: <AnalyticsItem webItems={webItems} />,
			allowed: !isViewMode && isWebItemAvailable,
		},
		{
			component: <PageHistoryMenuItem webItems={webItems} />,
			allowed: true,
		},
		{
			component: (
				<AdvancedDetailsMenuItem
					isSubMenuOpen={openSubMenu === SubMenuTypes.Advanced}
					handleMouseEnter={() => handleSubMenuMouseEnter(SubMenuTypes.Advanced)}
					handleClick={() => handleSubMenuItemClick(SubMenuTypes.Advanced)}
					webItems={webItems}
				/>
			),
			isSubMenu: true,
			allowed: isAllowed(Buttons.ADVANCED) && isWebItemAvailable,
		},
		{
			component: (
				<AttachmentsMenuItem
					contentId={contentId}
					spaceKey={spaceKey}
					webItems={webItems}
					isSubMenuOpen={openSubMenu === SubMenuTypes.Attachments}
					handleMouseEnter={() => handleSubMenuMouseEnter(SubMenuTypes.Attachments)}
					handleClick={() => handleSubMenuItemClick(SubMenuTypes.Attachments)}
				/>
			),
			isSubMenu: true,
			allowed: isAllowed(Buttons.ATTACHMENTS) && isWebItemAvailable,
		},
	];

	const getItems = (dialogs, forgeMenuItems) => {
		const groupItems = {
			[GroupItemsKey.PUBLISH]: getPublishGroupItems(),
			[GroupItemsKey.OWNERSHIP]: getOwnershipGroupItems(dialogs),
			[GroupItemsKey.PAGEACTIONS]: getPageActionGroupItems(dialogs),
			[GroupItemsKey.PAGEAPPS]: getPageAppsGroupItems(forgeMenuItems),
			[GroupItemsKey.OTHER]: getOtherGroupItems(dialogs),
		};

		return Object.entries(groupItems).reduce<Array<JSX.Element>>(
			(acc, [key, items], index, arr) => {
				const components = items
					.filter((item) => item.allowed)
					.map((item) => (item.isSubMenu ? item.component : menuItemWrapper(item.component)));

				if (components.length > 0) {
					acc.push(
						<DropdownItemGroup
							key={key}
							testId={`group-${key}`}
							hasSeparator={index > 0 && index <= arr.length - 1}
						>
							{components}
						</DropdownItemGroup>,
					);
				}

				return acc;
			},
			[],
		);
	};

	return (
		<ErrorBoundary attribution={Attribution.PAGE_MODES}>
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs) => (
					<ForgeContentTools
						spaceKey={spaceKey}
						contentId={contentId}
						onLoadComplete={() => setIsMenuOpen(false)}
						isDropdownMenuOpen={isMenuOpen}
						dialogs={dialogs}
					>
						{(forgeMenuItems) => (
							<SpotlightTarget name="live-pages-changeboarding.publish-button">
								<CommonDropdownMenu
									isMenuOpen={isMenuOpen}
									handleMenuOpenChange={handleMenuOpenChange}
								>
									{getItems(dialogs, forgeMenuItems)}
								</CommonDropdownMenu>
								<GeneralShortcutListener
									accelerator={LINK_TO_THIS_PAGE_SHORTCUT}
									listener={makeLinkToThisPageDialogOpener(dialogs)}
								/>
								{isAllowed(Buttons.ATTACHMENTS) && isWebItemAvailable && (
									<GeneralShortcutListener
										accelerator={VIEW_ATTACHMENTS_SHORTCUT}
										listener={navigateToAttachments}
									/>
								)}
							</SpotlightTarget>
						)}
					</ForgeContentTools>
				)}
			</Subscribe>
		</ErrorBoundary>
	);
};
